import { exceedsCapacityCents } from '@ovComponents/4-module/widgets/portfolio/components/allocationCapacity';
import {
  allocationCellKey,
  subAccountReportId,
} from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import {
  allocationCapacityByKey,
  buildChangedCustomAllocationCells,
  buildCustomAllocationSubAccounts,
  type CustomAllocationReport,
  type SubAccountCustomAllocationInput,
} from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import {
  type AllocationViolationReason,
  AllocationViolationReasons,
  evaluateAllocationViolations,
} from '@ovComponents/4-module/widgets/portfolio/components/computeAllocationHighlights';
import {
  type LockedMatrixCell,
  lockedMatrixRedistribute,
  type SolveStatus,
  SolveStatuses,
} from '@ovComponents/4-module/widgets/portfolio/components/lockedMatrixRedistribute';
import { tradeableAllocationSubAccounts } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import {
  fractionFromAmountCents,
  fractionToAmountCents,
  fractionToDisplayPercent,
} from '@utils/allocationUnits';
import { useToast } from 'hephaestus/web';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildSolvedViolationCells } from './buildSolvedViolationCells';
import { isCashHolding } from './cashHolding';
import { getPersistedFraction } from './getPersistedFraction';
import { type CellEditPatch, resolveCellEdit } from './resolveCellEdit';
import {
  restoredDraftPatch,
  type SavedAllocationDraft,
} from './restoredDraftPatch';
import { useCellStateActions } from './useCellStateActions';

const ToastSeverities = {
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
} as const satisfies Record<string, 'error' | 'info' | 'success' | 'warning'>;

const AllocationDraftActionTypes = {
  RESET: 'reset',
  PATCH: 'patch',
} as const;

// reasons is always non-empty here, with only 'weightSum'/'rowDrift'/'capacity' possible — no fallback branch needed.
const advisoryViolationMessageKey = (
  reasons: Set<AllocationViolationReason>,
): string => {
  if (reasons.size > 1) {
    return 'components:householdPortfolio.advisoryViolations.MULTIPLE';
  }
  if (reasons.has(AllocationViolationReasons.CAPACITY)) {
    return 'components:householdPortfolio.advisoryViolations.CAPACITY';
  }
  return reasons.has(AllocationViolationReasons.WEIGHT_SUM)
    ? 'components:householdPortfolio.advisoryViolations.WEIGHT_SUM'
    : 'components:householdPortfolio.advisoryViolations.ROW_DRIFT';
};

// lockedCells/lockedAmountCents/uploadedCells/draft/solveStatus always change together
// (commitCellEdit, runSolver, resetDraft), so they share one reducer instead of
// correlated useState calls.
type AllocationDraftState = {
  lockedCells: Map<string, number>;
  lockedAmountCents: Map<string, number>;
  uploadedCells: Set<string>;
  draft: Map<string, number>;
  solveStatus: SolveStatus | null;
  // An edit, release or upload the last solve hasn't absorbed yet.
  pendingRecalculation: boolean;
};

// Fresh Maps per call, not a shared singleton — avoids a future PATCH mutating a prior reset's state.
const createInitialAllocationDraftState = (): AllocationDraftState => ({
  lockedCells: new Map(),
  lockedAmountCents: new Map(),
  uploadedCells: new Set(),
  draft: new Map(),
  solveStatus: null,
  pendingRecalculation: false,
});

type AllocationDraftAction =
  | { type: typeof AllocationDraftActionTypes.RESET }
  | {
      type: typeof AllocationDraftActionTypes.PATCH;
      patch: Partial<Omit<AllocationDraftState, 'pendingRecalculation'>>;
    };

const allocationDraftReducer = (
  state: AllocationDraftState,
  action: AllocationDraftAction,
): AllocationDraftState => {
  switch (action.type) {
    case AllocationDraftActionTypes.RESET:
      return createInitialAllocationDraftState();
    case AllocationDraftActionTypes.PATCH:
      return {
        ...state,
        ...action.patch,
        // Every edit, release and upload discards the solve (solveStatus: null);
        // only an OK solve settles them. INFEASIBLE / FAILED leave them pending.
        pendingRecalculation:
          action.patch.solveStatus === undefined
            ? state.pendingRecalculation
            : action.patch.solveStatus !== SolveStatuses.OK,
      };
    default:
      return state;
  }
};

export const useCustomAllocationDraft = (
  portfolioReport: CustomAllocationReport,
  {
    onConfirm,
  }: {
    onConfirm: (
      subAccounts: SubAccountCustomAllocationInput[],
    ) => void | Promise<void>;
  },
) => {
  const { enqueueToast } = useToast();
  const { t } = useTranslation(['components']);
  const [confirming, setConfirming] = useState(false);
  const [draftState, dispatchDraft] = useReducer(
    allocationDraftReducer,
    createInitialAllocationDraftState(),
  );
  const {
    lockedCells,
    lockedAmountCents,
    uploadedCells,
    draft,
    solveStatus,
    pendingRecalculation,
  } = draftState;

  const customizableReport = useMemo(
    () => ({
      ...portfolioReport,
      subAccounts: tradeableAllocationSubAccounts(portfolioReport.subAccounts),
    }),
    [portfolioReport],
  );

  const members = useMemo(
    () =>
      (customizableReport.subAccounts ?? []).map((subAccount) => ({
        sa: subAccountReportId(subAccount),
        mvCents: subAccount.marketValueCents,
      })),
    [customizableReport.subAccounts],
  );

  const rowTargetFraction = useMemo(() => {
    const targets = new Map<string, number>();
    for (const holding of customizableReport.holdings ?? []) {
      const fp = holding.financialProduct.id;
      if (!fp || isCashHolding(fp, holding)) continue;
      targets.set(fp, holding.expectedPercentage);
    }
    return targets;
  }, [customizableReport.holdings]);

  const securityMassFraction =
    1 - (customizableReport.expectedCashPercentage ?? 0);

  const capacityByKey = useMemo(
    () => allocationCapacityByKey(portfolioReport),
    [portfolioReport],
  );

  const mvCentsBySubAccountId = useMemo(
    () => new Map(members.map((member) => [member.sa, member.mvCents])),
    [members],
  );

  const mvCentsOf = useCallback(
    (sa: string) => mvCentsBySubAccountId.get(sa) ?? 0,
    [mvCentsBySubAccountId],
  );

  const buildSaveCellOptions = useCallback(
    () => ({
      amountCentsByKey: lockedAmountCents,
      mvCentsBySubAccountId,
      securityMassFraction,
    }),
    [lockedAmountCents, mvCentsBySubAccountId, securityMassFraction],
  );

  const persistedFraction = useCallback(
    (sa: string, fp: string, modelTarget: number) =>
      getPersistedFraction(portfolioReport, sa, fp, modelTarget),
    [portfolioReport],
  );

  const displayFraction = useCallback(
    (sa: string, fp: string, modelTarget: number): number => {
      const key = allocationCellKey(sa, fp);
      const lockCents = lockedAmountCents.get(key);
      if (lockCents !== undefined) {
        return fractionFromAmountCents(lockCents, mvCentsOf(sa));
      }
      const stagedFraction = lockedCells.get(key);
      if (stagedFraction !== undefined) {
        return stagedFraction;
      }
      if (solveStatus === SolveStatuses.OK) {
        const solvedFraction = draft.get(key);
        if (solvedFraction !== undefined) {
          return solvedFraction;
        }
      }
      return persistedFraction(sa, fp, modelTarget);
    },
    [
      solveStatus,
      draft,
      lockedCells,
      lockedAmountCents,
      persistedFraction,
      mvCentsOf,
    ],
  );

  const isLocked = useCallback(
    (sa: string, fp: string) => lockedCells.has(allocationCellKey(sa, fp)),
    [lockedCells],
  );

  const solvedViolations = useMemo(() => {
    if (solveStatus !== SolveStatuses.OK || draft.size === 0) {
      return {
        violationCells: new Set<string>(),
        reasons: new Set<AllocationViolationReason>(),
      };
    }

    return evaluateAllocationViolations({
      members,
      rowTargetFraction,
      cells: buildSolvedViolationCells({
        subAccounts: customizableReport.subAccounts,
        rowTargetFraction,
        draft,
        persistedFractionOf: persistedFraction,
      }),
      securityMassFraction,
      capacityFractionByKey: capacityByKey,
    });
  }, [
    solveStatus,
    draft,
    customizableReport.subAccounts,
    rowTargetFraction,
    persistedFraction,
    members,
    securityMassFraction,
    capacityByKey,
  ]);

  const violationCells = solvedViolations.violationCells;
  // Only the solver can push a cell over its cap (the commit path rejects those edits).
  // Save would fail with CAPACITY_BREACH, so it stays disabled until the adviser fixes it.
  const hasCapacityBreach = solvedViolations.reasons.has(
    AllocationViolationReasons.CAPACITY,
  );

  const isInViolation = useCallback(
    (sa: string, fp: string) => {
      const key = allocationCellKey(sa, fp);
      return violationCells.has(key) && !lockedCells.has(key);
    },
    [violationCells, lockedCells],
  );

  const hasChanges = useMemo(() => {
    if (solveStatus !== SolveStatuses.OK) return false;
    return (
      buildChangedCustomAllocationCells(
        draft,
        customizableReport,
        buildSaveCellOptions(),
      ).length > 0
    );
  }, [solveStatus, draft, customizableReport, buildSaveCellOptions]);

  const canConfirm =
    solveStatus === SolveStatuses.OK && hasChanges && !hasCapacityBreach;
  const canRecalculate =
    lockedCells.size > 0 && solveStatus !== SolveStatuses.OK;
  // hasChanges alone misses locks pending a solve; lockedCells.size alone
  // misses a solved lock that nets back to its persisted value.
  const hasUnsavedWork =
    hasChanges || (lockedCells.size > 0 && solveStatus !== SolveStatuses.OK);
  // Workflow gating: unlike canConfirm, Continue doesn't need a change, only
  // nothing waiting on a Recalculate. Never true while needsRecalculation is.
  const canContinue =
    !pendingRecalculation &&
    solveStatus !== SolveStatuses.INFEASIBLE &&
    solveStatus !== SolveStatuses.FAILED &&
    !hasCapacityBreach;

  const resetDraft = useCallback(() => {
    dispatchDraft({ type: AllocationDraftActionTypes.RESET });
  }, []);

  // Compared by content — a market value change mid-edit invalidates
  // the solver's cents-domain math and any staged dollar lock.
  const memberMvCentsKey = useMemo(
    () => members.map((member) => `${member.sa}:${member.mvCents}`).join(','),
    [members],
  );

  // Deliberately excludes allocationMethodology: a background refresh that
  // only flips the methodology must not wipe staged locks.
  // biome-ignore lint/correctness/useExhaustiveDependencies: report id and member MV are re-run triggers, not values read by the effect
  useEffect(() => {
    resetDraft();
  }, [portfolioReport?.id, memberMvCentsKey, resetDraft]);

  const buildSolverCells = useCallback(
    (locks: Map<string, number>): LockedMatrixCell[] => {
      const cells: LockedMatrixCell[] = [];

      for (const subAccount of customizableReport.subAccounts ?? []) {
        const sa = subAccountReportId(subAccount);

        for (const holding of subAccount.holdings ?? []) {
          const fp = holding.financialProduct.id;
          if (!fp || isCashHolding(fp, holding)) continue;

          const modelTarget = rowTargetFraction.get(fp) ?? 0;
          const key = allocationCellKey(sa, fp);
          const fraction =
            locks.get(key) ?? persistedFraction(sa, fp, modelTarget);

          cells.push({
            sa,
            fp,
            amountCents:
              lockedAmountCents.get(key) ??
              fractionToAmountCents(fraction, mvCentsOf(sa)),
          });
        }
      }

      return cells;
    },
    [
      customizableReport.subAccounts,
      rowTargetFraction,
      persistedFraction,
      mvCentsOf,
      lockedAmountCents,
    ],
  );

  const applySolvedCells = useCallback(
    (solvedCells: LockedMatrixCell[]) => {
      const nextDraft = new Map<string, number>();
      for (const cell of solvedCells) {
        nextDraft.set(
          allocationCellKey(cell.sa, cell.fp),
          fractionFromAmountCents(cell.amountCents, mvCentsOf(cell.sa)),
        );
      }
      dispatchDraft({
        type: AllocationDraftActionTypes.PATCH,
        patch: { draft: nextDraft, solveStatus: SolveStatuses.OK },
      });
    },
    [mvCentsOf],
  );

  const showAdvisoryViolationsIfNeeded = useCallback(
    (solvedCells: LockedMatrixCell[], locks: Map<string, number>) => {
      const cells = solvedCells.map((cell) => ({
        sa: cell.sa,
        fp: cell.fp,
        fraction: fractionFromAmountCents(cell.amountCents, mvCentsOf(cell.sa)),
      }));
      const { violationCells: violations, reasons } =
        evaluateAllocationViolations({
          members,
          rowTargetFraction,
          cells,
          securityMassFraction,
          capacityFractionByKey: capacityByKey,
        });

      const hasNonLockedViolation = [...violations].some(
        (key) => !locks.has(key),
      );
      if (!hasNonLockedViolation || reasons.size === 0) {
        return;
      }

      enqueueToast(t(advisoryViolationMessageKey(reasons)), {
        variant: ToastSeverities.WARNING,
      });
    },
    [
      members,
      rowTargetFraction,
      securityMassFraction,
      capacityByKey,
      mvCentsOf,
      enqueueToast,
      t,
    ],
  );

  const runSolver = useCallback(
    (locks: Map<string, number>) => {
      const cells = buildSolverCells(locks);
      const { status, after } = lockedMatrixRedistribute({
        members,
        cells,
        rowTargetFraction,
        lockedKeys: new Set(locks.keys()),
        securityMassFraction,
      });

      if (status === SolveStatuses.OK) {
        applySolvedCells(after);
        showAdvisoryViolationsIfNeeded(after, locks);
        return;
      }

      dispatchDraft({
        type: AllocationDraftActionTypes.PATCH,
        patch: { solveStatus: status, draft: new Map() },
      });

      if (status === SolveStatuses.INFEASIBLE) {
        enqueueToast(
          t('components:householdPortfolio.errors.LOCKS_INFEASIBLE'),
          { variant: ToastSeverities.ERROR },
        );
      } else if (status === SolveStatuses.FAILED) {
        enqueueToast(t('components:householdPortfolio.errors.SOLVE_FAILED'), {
          variant: ToastSeverities.ERROR,
        });
      }
    },
    [
      buildSolverCells,
      members,
      rowTargetFraction,
      securityMassFraction,
      applySolvedCells,
      showAdvisoryViolationsIfNeeded,
      enqueueToast,
      t,
    ],
  );

  const rejectOverCapacity = useCallback(
    (key: string, mv: number, fraction: number, amountCents?: number) => {
      const capacity = capacityByKey.get(key);
      const requestedCents = amountCents ?? fractionToAmountCents(fraction, mv);
      if (
        capacity === undefined ||
        !exceedsCapacityCents(requestedCents, capacity, mv)
      ) {
        return false;
      }
      enqueueToast(
        t('components:householdPortfolio.capacityLimit', {
          max: fractionToDisplayPercent(capacity),
        }),
        { variant: ToastSeverities.WARNING },
      );
      return true;
    },
    [capacityByKey, enqueueToast, t],
  );

  const commitCellEdit = useCallback(
    (sa: string, fp: string, fraction: number, amountCents?: number) => {
      const key = allocationCellKey(sa, fp);
      const modelTarget = rowTargetFraction.get(fp) ?? 0;
      const mv = mvCentsOf(sa);

      if (rejectOverCapacity(key, mv, fraction, amountCents)) return;

      const patch = resolveCellEdit({
        key,
        fraction,
        amountCents,
        mv,
        original: persistedFraction(sa, fp, modelTarget),
        lockedCells,
        lockedAmountCents,
        uploadedCells,
        draft,
        solveStatus,
      });
      if (patch) {
        dispatchDraft({ type: AllocationDraftActionTypes.PATCH, patch });
      }
    },
    [
      lockedCells,
      lockedAmountCents,
      uploadedCells,
      rowTargetFraction,
      persistedFraction,
      mvCentsOf,
      solveStatus,
      draft,
      rejectOverCapacity,
    ],
  );

  const applyPatch = useCallback((patch: CellEditPatch) => {
    dispatchDraft({ type: AllocationDraftActionTypes.PATCH, patch });
  }, []);

  const cellStateActions = useCellStateActions({
    lockedCells,
    lockedAmountCents,
    uploadedCells,
    mvCentsOf,
    rejectOverCapacity,
    applyPatch,
  });

  const recalculate = useCallback(() => {
    if (lockedCells.size === 0) return;
    runSolver(lockedCells);
  }, [lockedCells, runSolver]);

  const restoreDraft = useCallback(
    (saved: SavedAllocationDraft) => {
      dispatchDraft({
        type: AllocationDraftActionTypes.PATCH,
        patch: restoredDraftPatch(saved, mvCentsOf),
      });
    },
    [mvCentsOf],
  );

  // Unlike recalculate, solves with zero locks too: releasing every cell
  // re-solves the whole matrix as Derived.
  const recalculateAll = useCallback(() => {
    runSolver(lockedCells);
  }, [lockedCells, runSolver]);

  const confirm = useCallback(async () => {
    const changedCells = buildChangedCustomAllocationCells(
      draft,
      customizableReport,
      buildSaveCellOptions(),
    );
    if (changedCells.length === 0) {
      enqueueToast(t('components:householdPortfolio.errors.BAD_USER_INPUT'), {
        variant: ToastSeverities.ERROR,
      });
      return false;
    }

    setConfirming(true);
    try {
      await onConfirm(buildCustomAllocationSubAccounts(changedCells));
      resetDraft();
      return true;
    } finally {
      setConfirming(false);
    }
  }, [
    draft,
    customizableReport,
    buildSaveCellOptions,
    onConfirm,
    resetDraft,
    enqueueToast,
    t,
  ]);

  return {
    lockedCells,
    isLocked,
    isInViolation,
    solveStatus,
    canConfirm,
    canRecalculate,
    hasChanges,
    hasUnsavedWork,
    displayFraction,
    commitCellEdit,
    ...cellStateActions,
    recalculate,
    needsRecalculation: pendingRecalculation,
    recalculateAll,
    canContinue,
    restoreDraft,
    resetDraft,
    confirming,
    confirm,
  };
};
